import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import SEO from '../components/SEO.js';
import GuideDownloader from '../components/GuideDownloader.jsx';
import AdUnit from '../components/AdUnit.jsx';
import img from '../RapidMind 2025.png'; // Reuse existing logo
const LandingPage = () => {
  const navigate = useNavigate();
  const { user, refreshUserData } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  // Check if onboarding already complete (e.g., if businessEmail exists)
  React.useEffect(() => {
    if (user?.businessEmail) {
      navigate('/home'); // Or original Home route; adjust if needed
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName || !businessEmail) {
      setError('Please fill in both fields.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          firstName,
          businessEmail,
          onboardingComplete: true, // Add flag if backend supports
        }),
      });

      if (response.ok) {
        // Refresh user data
        await refreshUserData();
        // Set local flag for immediate redirect
        localStorage.setItem('onboardingComplete', 'true');
        setSubmitSuccess(true);
        // Simulate guide download or email trigger
        console.log('Guide sent to:', businessEmail);
        // Redirect after short delay
        setTimeout(() => {
          navigate('/home'); // Redirect to original dashboard or workspace
        }, 2000);
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (err) {
      setError('Submission failed. Please try again.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', textAlign: 'center', color: '#fff', background: '#0f172a' }}>
        <div>
          <h2>🎉 Success!</h2>
          <p>Your profile has been updated. Check your email for the FREE AI Content Guide!</p>
          <p>Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="RapidMind AI - Transform Ideas into AI-Powered Content Machines"
        description="Turn your ideas into stunning content with RapidMind AI. Generate images, text, and more with advanced AI tools. Free to start, proven results for creators. 250% lead increase guaranteed."
        keywords="AI tools, content creation, AI image generator, AI workspace, rapidmind ai, free ai tools, content automation, AI for creators, business AI solutions"
        image="/RapidMind 2025.png"
        type="website"
      />
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        color: '#fff',
        fontFamily: 'Arial, sans-serif',
        padding: '0',
        overflowX: 'hidden'
      }}>
      {/* Header with Logo */}
      <header style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: 'rgba(15, 23, 42, 0.8)',
        gap: '10px'
      }}>
        <img src={img} alt="RapidMind Logo" style={{ maxWidth: '150px', height: 'auto' }} />
        <AdUnit />
      </header>

      {/* Hero Section */}
      <section style={{ 
        display: 'flex', 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '40px 20px', 
        flexDirection: window.innerWidth < 768 ? 'column' : 'row', 
        alignItems: 'center', 
        gap: '40px' 
      }}>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 'bold',
            marginBottom: '20px',
            lineHeight: 1.2
          }}>
            Turn Your Ideas Into <span style={{ color: '#3b82f6' }}>an AI-Powered Content Machine</span>
          </h1>
          <p style={{ 
            fontSize: '1.2rem', 
            marginBottom: '30px', 
            lineHeight: 1.6 
          }}>
            Download the FREE guide that's helped 500+ creators increase qualified content leads by 250% with proven AI web application strategies.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
            <GuideDownloader buttonText="Download FREE Guide Now" />
            <div style={{ display: 'flex', gap: '20px', fontSize: '0.9rem', opacity: 0.8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <a href="/about" style={{ color: '#3b82f6', textDecoration: 'none' }}>Learn More About Us</a>
              <a href="/pricing" style={{ color: '#3b82f6', textDecoration: 'none' }}>View Pricing</a>
              <a href="/login" style={{ color: '#3b82f6', textDecoration: 'none' }}>Login</a>
            </div>
          </div>
          <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>No credit card required</p>
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <h3 style={{ marginBottom: '20px', fontSize: '1.5rem' }}>Get Instant Access</h3>
          <p style={{ marginBottom: '10px', fontSize: '0.9rem' }}>Join 500+ creators who transformed their online presence</p>
          <form id="form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '300px', margin: '0 auto' }}>
            <input
              type="text"
              placeholder="First Name *"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={{
                padding: '12px',
                border: '1px solid #475569',
                borderRadius: '6px',
                background: '#1e293b',
                color: '#fff',
                fontSize: '1rem'
              }}
              required
            />
            <input
              type="email"
              placeholder="Business Email *"
              value={businessEmail}
              onChange={(e) => setBusinessEmail(e.target.value)}
              style={{
                padding: '12px',
                border: '1px solid #475569',
                borderRadius: '6px',
                background: '#1e293b',
                color: '#fff',
                fontSize: '1rem'
              }}
              required
            />
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                background: isSubmitting ? '#94a3b8' : '#3b82f6',
                color: '#fff',
                border: 'none',
                padding: '15px',
                fontSize: '1.1rem',
                borderRadius: '8px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Yes, I want my FREE guide now!'}
            </button>
            {error && <p style={{ color: '#ef4444', fontSize: '0.9rem', textAlign: 'center' }}>{error}</p>}
          </form>
        </div>
      </section>

      {/* What You'll Learn */}
      <section style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '60px 20px', 
        background: 'rgba(30, 41, 59, 0.5)',
        textAlign: 'center'
      }}>
        <h2 style={{ 
          fontSize: '2.5rem', 
          marginBottom: '40px', 
          lineHeight: 1.2 
        }}>
          What You'll Learn in This FREE Guide
        </h2>
        <p style={{ fontSize: '1.1rem', marginBottom: '40px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
          Everything you need to transform your business with AI applications that deliver proven ROI.
        </p>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '20px', 
          maxWidth: '1000px', 
          margin: '0 auto' 
        }}>
          <div style={{ padding: '20px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}>
            <span style={{ fontSize: '2rem', marginBottom: '10px', display: 'block' }}>✅</span>
            <h3>Proven ROI Strategies</h3>
            <p>Discover AI tactics that guarantee measurable returns on your content creation.</p>
          </div>
          <div style={{ padding: '20px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}>
            <span style={{ fontSize: '2rem', marginBottom: '10px', display: 'block' }}>✅</span>
            <h3>Mobile First Implementation</h3>
            <p>Build AI tools that work seamlessly across all devices for maximum reach.</p>
          </div>
          <div style={{ padding: '20px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}>
            <span style={{ fontSize: '2rem', marginBottom: '10px', display: 'block' }}>✅</span>
            <h3>Cost Effective Tech Stack</h3>
            <p>Integrate powerful AI without breaking the bank – scalable and efficient.</p>
          </div>
          <div style={{ padding: '20px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}>
            <span style={{ fontSize: '2rem', marginBottom: '10px', display: 'block' }}>✅</span>
            <h3>Lead Generation Automation</h3>
            <p>Automate content leads and engagement with smart AI workflows.</p>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '60px 20px', 
        textAlign: 'center',
        background: 'rgba(15, 23, 42, 0.8)'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <blockquote style={{ 
            fontSize: '1.3rem', 
            fontStyle: 'italic', 
            marginBottom: '30px', 
            lineHeight: 1.5 
          }}>
            "This guide completely transformed how we approach our online presence. Within 90 days of implementing these strategies, we saw a 250% increase in qualified leads and our revenue jumped by 40%. The mobile-first approach alone doubled our conversion rate."
          </blockquote>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
            <div style={{ 
              background: '#3b82f6', 
              width: '50px', 
              height: '50px', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
              MJ
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 'bold' }}>Maria Johnson</p>
              <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.8 }}>Manufacturing Solutions</p>
              <div style={{ display: 'flex', gap: '2px', justifyContent: 'center', marginTop: '5px' }}>
                {[...Array(5)].map((_, i) => <span key={i} style={{ color: '#fbbf24', fontSize: '1.2rem' }}>⭐</span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '60px 20px', 
        textAlign: 'center' 
      }}>
        <h2 style={{ 
          fontSize: '2.5rem', 
          marginBottom: '20px', 
          lineHeight: 1.2 
        }}>
          Why Creators Choose RapidMind
        </h2>
        <p style={{ fontSize: '1.1rem', marginBottom: '40px' }}>
          Unlike generic AI tools, we specialize in solutions that drive real business growth for creators like you.
        </p>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '30px', 
          maxWidth: '1000px', 
          margin: '0 auto' 
        }}>
          <div style={{ padding: '30px 20px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>👥</div>
            <h3>SMB Specialist</h3>
            <p>7+ years serving small-medium creators with proven results</p>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6', marginTop: '10px' }}>500+</div>
            <p style={{ fontSize: '0.8rem', opacity: 0.8 }}>Creators Served</p>
          </div>
          <div style={{ padding: '30px 20px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>📱</div>
            <h3>Mobile First</h3>
            <p>Every solution optimized for 100% of customer devices</p>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6', marginTop: '10px' }}>250%</div>
            <p style={{ fontSize: '0.8rem', opacity: 0.8 }}>Avg Lead Increase</p>
          </div>
          <div style={{ padding: '30px 20px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🛡️</div>
            <h3>Risk-Free</h3>
            <p>Strategies tested on 100+ business logics</p>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6', marginTop: '10px' }}>Tested</div>
            <p style={{ fontSize: '0.8rem', opacity: 0.8 }}>Strategies</p>
          </div>
          <div style={{ padding: '30px 20px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>⚡</div>
            <h3>Fast Implementation</h3>
            <p>Designed for quick business outcomes, not months</p>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6', marginTop: '10px' }}>90</div>
            <p style={{ fontSize: '0.8rem', opacity: 0.8 }}>Days to Results</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ 
        background: '#1e293b', 
        padding: '60px 20px', 
        textAlign: 'center' 
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            marginBottom: '20px', 
            lineHeight: 1.2 
          }}>
            Ready to Transform Your Business?
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '40px' }}>
            Don't let another day pass watching competitors capture the leads that should be yours. Download your FREE guide now and start implementing these proven strategies today.
          </p>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: window.innerWidth < 768 ? 'column' : 'row', gap: '15px', maxWidth: '500px', margin: '0 auto', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={{
                flex: 1,
                padding: '12px',
                border: '1px solid #475569',
                borderRadius: '6px',
                background: '#334155',
                color: '#fff',
                fontSize: '1rem',
                minWidth: '200px'
              }}
              required
            />
            <input
              type="email"
              placeholder="Business Email"
              value={businessEmail}
              onChange={(e) => setBusinessEmail(e.target.value)}
              style={{
                flex: 1,
                padding: '12px',
                border: '1px solid #475569',
                borderRadius: '6px',
                background: '#334155',
                color: '#fff',
                fontSize: '1rem',
                minWidth: '200px'
              }}
              required
            />
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                background: isSubmitting ? '#94a3b8' : '#3b82f6',
                color: '#fff',
                border: 'none',
                padding: '12px 30px',
                fontSize: '1.1rem',
                borderRadius: '8px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Yes, I want my FREE guide now!'}
            </button>
          </form>
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            justifyContent: 'center', 
            marginTop: '20px', 
            fontSize: '0.9rem', 
            opacity: 0.8,
            flexWrap: 'wrap'
          }}>
            <span>Instant Download</span>
            <span>|</span>
            <span>No Spam</span>
            <span>|</span>
            <span>Free</span>
          </div>
        </div>
      </section>

      </div>
    </>
  );
};

export default LandingPage;
